
import type {
  BaseErrorResponse,
  BulkCreatePayoutReq,
  EmptySuccessResponse,
  GetPayoutListRes,
  PayoutExportReq,
  ProcessPaymentsReq,
  UpdatePayoutReq,
  ExportHeadersResponse,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function usePayout() {
  class Payout<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerProcessPayouts
     * @summary Starts payment operation
     * @request POST:/api3/payout/pay
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerProcessPayouts = (data: ProcessPaymentsReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/payout/pay',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerStartExport
     * @summary Starts export operation
     * @request POST:/api3/payout/export
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerStartExport = (data: PayoutExportReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/payout/export',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerGetExportHeaders
     * @summary Get export headers
     * @request GET:/api3/payout/export/headers
     * @secure
     * @response `200` `ExportHeadersResponse`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerGetExportHeaders = (params: RequestParams = {}) =>
      this.request<ExportHeadersResponse, BaseErrorResponse>({
        path: '/api3/payout/export/headers',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerGetPayoutsList
     * @summary Get payout list
     * @request GET:/api3/payout
     * @secure
     * @response `200` `GetPayoutListRes`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerGetPayoutsList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Sort options
         * @example {"orderBy":{"payout.created_at":"ASC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"wallet-type.title","value":"Bonus","operation":"like"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPayoutListRes, BaseErrorResponse>({
        path: '/api3/payout',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerUpdatePayout
     * @summary Update payout
     * @request PUT:/api3/payout/{id}/process
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerUpdatePayout = (id: number, data: UpdatePayoutReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/payout/${id}/process`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name PayoutControllerBulkCreatePayout
     * @summary Bulk create payout
     * @request POST:/api3/payout/bulk-create
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    payoutControllerBulkCreatePayout = (data: BulkCreatePayoutReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/payout/bulk-create',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });
  }

  return new Payout();
}
